import { useEffect, useRef, useState } from "react";
import NotificationService from "../services/notificationService";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const useNotifications = (trigger) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await NotificationService.getAdminNotifications();
      const data = response?.data || [];
      setNotifications(data);
      setUnreadCount(data.filter((n) => n.seen === 0).length);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const optimisticUpdate = notifications.map((n) =>
        n.id === notificationId ? { ...n, seen: 1 } : n,
      );
      setNotifications(optimisticUpdate);
      setUnreadCount(optimisticUpdate.filter((n) => n.seen === 0).length);

      const response = await NotificationService.markNotificationAsRead(notificationId);

      if (!response || response.error) {
        throw new Error("API call failed");
      }
    } catch {
      const revertedNotifications = notifications.map((n) =>
        n.id === notificationId ? { ...n, seen: 0 } : n,
      );
      setNotifications(revertedNotifications);
      setUnreadCount(
        revertedNotifications.filter((n) => n.seen === 0).length,
      );
    }
  };

  useEffect(() => {
    const startPolling = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopPolling();
      } else {
        fetchNotifications();
        startPolling();
      }
    };

    fetchNotifications();
    if (document.visibilityState !== "hidden") {
      startPolling();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [trigger]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    setNotifications,
    loading,
    refreshNotifications: fetchNotifications,
  };
};

export default useNotifications;
